import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
} from "framer-motion";
import { supabase } from "./supabaseClient";
import TeamCard from "./components/TeamCard";
import "./App.css";
import Footer from "./components/Footer";
import Navbar from "./components/Navbar";
import TopBar from "./components/TopBar";
import { ShieldCheck, Star,} from "lucide-react";

function App() {
  const [staff, setStaff] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [index, setIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [20, 0, -20]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);

  const partners = [
    { name: "Kenindia", src: "" },
    {
      name: "Geminia",
      src: "https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767855369/geminia_smbdhh.png",
    },
    {
      name: "First Assurance",
      src: "https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767855369/first-assurance_ws1ico.png",
    },
    { name: "Mayfair", src: "../assets/mayfair.svg" },
    {
      name: "Occidental",
      src: "https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767855369/occidental_mlkoz3.png",
    },
    {
      name: "MUA",
      src: "https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767855369/mua_chnbhx.png",
    },
    {
      name: "APA",
      src: "https://res.cloudinary.com/dxgkcyfrl/image/upload/v1767855369/apa_l4mklp.png",
    },
  ];

  const products = [
    {
      title: "Business",
      type: "Quantum Protection",
      gridClass: "bento-main",
      desc: "Comprehensive risk architecture designed for modern enterprises.",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <rect x="2" y="7" width="20" height="14" rx="2" />
          <path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" />
        </svg>
      ),
    },
    {
      title: "Estate",
      type: "Home Security",
      gridClass: "bento-main",
      desc: "Protecting your sanctuary with smart-integrated coverage.",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      title: "Auto",
      type: "Smart Mobility",
      gridClass: "bento-small",
      desc: "AI-driven coverage for the modern driver.",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-1.1 0-2 .9-2 2v7h2m14 0c0 1.1-.9 2-2 2s-2-.9-2-2m4 0c0-1.1-.9-2-2-2s-2 .9-2 2m-10 0c0 1.1-.9(2-2) 2s-2-.9-2-2m4 0c0-1.1-.9-2-2-2s-2 .9-2 2" />
        </svg>
      ),
    },
    {
      title: "Health",
      type: "Vital Shield",
      gridClass: "bento-small",
      desc: "Global wellness tracking and instant hospital coverage.",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <line x1="22" y1="12" x2="18" y2="12" />
          <line x1="15" y1="21" x2="9" y2="3" />
          <line x1="6" y1="12" x2="2" y2="12" />
        </svg>
      ),
    },
    {
      title: "Life & Legacy",
      type: "Generational Protection",
      gridClass: "bento-wide",
      desc: "Building a bridge for those who matter most.",
      icon: (
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.2"
        >
          <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
        </svg>
      ),
    },
  ];

  const coreValues = [
    {
      title: "Mission",
      desc: "Rift Nakuru Insurance Agencies Ltd is a professional company providing exceptional insurance solutions through comprehensive risk assessments, dedicated customer service, and a commitment to exceeding expectations, ensuring clients recieve the right coverage at the most competitive rates.",
      img: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800",
    },
    {
      title: "Vision",
      desc: "Our Vision is to be the leading provider of insurance solutions in the region, with a commitment to providing clients with the best possible insurance coverage at the most competitive rates.",
      img: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=800",
    },
    {
      title: "Motto",
      desc: "The company believes in the motto \"Securing your future\" where our rates and services are tough to beat, and where security is a priority, not an option.",
      img: "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=800",
    },
  ];

  const staggerContainer = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 },
    },
  };

  const smoothSpringReveal = {
    hidden: { opacity: 0, scale: 0.9, filter: "blur(10px)", y: 20 },
    visible: {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      y: 0,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  const springTransition = { type: "spring", stiffness: 300, damping: 30 };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    async function fetchData() {
      const { data: staffData } = await supabase
        .from("staff")
        .select("*")
        .gte("priority", 1)
        .order("priority", { ascending: true });
      if (staffData) setStaff(staffData);

      const { data: testData } = await supabase
        .from("testimonials")
        .select("*");
      if (testData) setTestimonials(testData);
    }
    fetchData();
  }, []);

  useEffect(() => {
    if (testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials]);

  const current =
    testimonials && testimonials.length > 0 ? testimonials[index] : null;

  return (
    <div className="App">
      <TopBar />
      <Navbar />

      <main className="rift-experience">
        <div className="sticky-parent">
          <section className="hero-viewport">
            <div className="hero-bg-layer">
              <div className="hero-overlay-curtain"></div>
              <motion.img
                initial={{ scale: 1.2 }}
                animate={{ scale: 1 }}
                transition={{ duration: 2.5 }}
                src="https://images.unsplash.com/photo-1556742044-3c52d6e88c62?auto=format&fit=crop&w=1920"
              />
            </div>
            <div className="hero-foreground">
              <div className="hero-layout">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="hero-text-content"
                >
                  <h1 className="hero-title">
                    Architecting <br />
                    <span className="glow-text">Certainty.</span>
                  </h1>
                  <p className="hero-paragraph">
                    Engineering risk frameworks to protect your enterprise and
                    family legacy.
                  </p>
                  <div className="hero-cta">
                    <button className="btn-premium">Secure Future</button>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        </div>

        <motion.section
          className="bento-reveal"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
        >
          <div className="bento-grid-layout">
            {products.map((item, idx) => (
              <motion.div
                key={idx}
                className={`bento-tile ${item.gridClass}`}
                variants={smoothSpringReveal}
                whileHover={{ y: -10 }}
              >
                <div className="tile-top">
                  <div className="tile-icon">{item.icon}</div>
                  <span className="tile-badge">{item.type}</span>
                </div>
                <div className="tile-body">
                  <h3 className="poppins-semibold">{item.title}</h3>
                  <p className="tile-desc">{item.desc}</p>
                </div>
                <div className="tile-footer">
                  <button className="tile-action-btn">
                    Get A Quote <span className="arrow">→</span>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </main>

      <motion.section
        className="who-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.4 }}
        variants={smoothSpringReveal}
      >
        <div className="who-container">
          <div className="who-visual">
            <div className="image-stack">
              <div className="main-frame">
                <img
                  src="https://lirp.cdn-website.com/8825f8bf/dms3rep/multi/opt/Rectangle+3-348h.jpg"
                  alt="Our Team"
                  className="img-reveal"
                />
              </div>
              <div className="experience-badge">
                <span className="years">20+</span>
                <span className="label">Years of Excellence</span>
              </div>
            </div>
          </div>
          <div className="who-content">
            <span className="who-eyebrow">SINCE 1978</span>
            <h2 className="who-title">
              We are in the business of{" "}
              <span className="blue-text">helping people.</span>
            </h2>
            <p className="who-para">
              Rift Nakuru Insurance Agencies Limited is a registered Insurance
              Agency founded in 1978.
            </p>
            <button className="read-more-btn">Read Our Story</button>
          </div>
        </div>
      </motion.section>

      <section className="partners-carousel-area">
        <div className="infinite-slider">
          <div className="slider-track">
            {[...partners, ...partners].map((partner, idx) => (
              <div className="partner-slide" key={idx}>
                <div className="logo-wrapper">
                  <img
                    src={partner.src}
                    alt={partner.name}
                    className="partner-logo-gray"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <motion.section
        className="core-values-section"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <div className="values-grid">
          {coreValues.map((item, idx) => (
            <motion.div
              key={idx}
              className="value-card"
              variants={smoothSpringReveal}
              whileHover={!isMobile ? "hover" : ""}
            >
              <div className="value-image-wrapper">
                <motion.img
                  src={item.img}
                  alt={item.title}
                  variants={{
                    hover: { scale: 1.1, filter: "brightness(0.3) blur(2px)" },
                  }}
                  transition={springTransition}
                />
              </div>
              <div className="value-content">
                <motion.div
                  className="content-shifter"
                  variants={{ hover: { y: -15 } }}
                  animate={isMobile ? { y: 0 } : undefined}
                  transition={springTransition}
                >
                  <motion.h3 className="value-title">{item.title}</motion.h3>
                  <motion.p
                    className="value-desc"
                    animate={isMobile ? { opacity: 1, height: "auto" } : {}}
                  >
                    {item.desc}
                  </motion.p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.section>

      <motion.div
        className="team-card-system"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={staggerContainer}
      >
        <div className="team-inner">
          <motion.div
            variants={smoothSpringReveal}
            className="team-header-block"
          >
            <span className="tag-line">OUR INNOVATORS</span>
            <h2 className="title-display">
              Meet Our Team <br /> <span className="accent">Trailblazers</span>
            </h2>
          </motion.div>
          <div className="team-grid-layout">
            {staff.slice(0, 4).map((member) => (
              <TeamCard
                key={member.id}
                member={member}
                variants={smoothSpringReveal}
              />
            ))}
          </div>
          <motion.div variants={smoothSpringReveal} className="team-footer">
            <Link to="/about-us/our-leadership" className="expand-team-btn">
              See Whole Team →
            </Link>
          </motion.div>
        </div>
      </motion.div>

      <section className="insure-testimonials-3d" ref={sectionRef}>
        <div className="mesh-gradient-overlay"></div>
        <div className="floating-particle p1"></div>
        <div className="floating-particle p2"></div>
        <div className="floating-particle p3"></div>

        <motion.div style={{ opacity }} className="section-header-3d">
          <span className="sub-title">TRUSTED PROTECTION</span>
          <h2 className="main-title">Client Perspectives</h2>
        </motion.div>

        <div className="carousel-wrapper-3d">
          <AnimatePresence mode="wait">
            {current && (
              <motion.div
                key={current.id}
                style={{ rotateX, scale, perspective: 1000 }}
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="insure-3d-card"
              >
                <div className="card-inner-glow"></div>
                <div className="floating-icon-3d">
                  <ShieldCheck size={32} color="#ffffff" />
                </div>
                <div className="stars-row-3d">
                  {[...Array(Number(current.rating || 5))].map((_, i) => (
                    <Star key={i} size={14} fill="#eab308" color="#eab308" />
                  ))}
                </div>
                <p className="insure-quote-3d">"{current.text}"</p>
                <div className="author-info-3d">
                  <h4>{current.author}</h4>
                  <span>Verified Policyholder</span>
                </div>
                <div className="premium-progress-container">
                  <div className="track-label">
                    <span>Next Insight</span>
                    <span>
                      0{index + 1} / 0{testimonials.length}
                    </span>
                  </div>

                  <div className="modern-timer-track">
                    <motion.div
                      className="modern-timer-fill"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 5, ease: "linear" }}
                      key={current.id}
                    >
                      <div className="comet-head"></div>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          <button
            className="see-all-3d-btn"
            onClick={() => (window.location.href = "/testimonials")}
          >
            Explore All Reviews
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default App;
